import { create } from 'zustand'
import type { Swarm, SwarmStatus, WebSocketMessage } from '../types'

interface SwarmState {
  swarms: Swarm[]
  selectedSwarm: Swarm | null
  isLoading: boolean
  error: string | null
  websocket: WebSocket | null
  liveUpdates: Map<string, any>
  
  // Actions
  setSwarms: (swarms: Swarm[]) => void
  addSwarm: (swarm: Swarm) => void
  updateSwarm: (id: string, updates: Partial<Swarm>) => void
  removeSwarm: (id: string) => void
  setSelectedSwarm: (swarm: Swarm | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  
  // WebSocket
  connectWebSocket: (swarmId: string, token: string) => void
  disconnectWebSocket: () => void
  handleWebSocketMessage: (message: WebSocketMessage) => void
  
  // Computed
  getSwarmsByStatus: (status: SwarmStatus) => Swarm[]
  getActiveSwarms: () => Swarm[]
}

export const useSwarmStore = create<SwarmState>()((set, get) => ({
  swarms: [],
  selectedSwarm: null,
  isLoading: false,
  error: null,
  websocket: null,
  liveUpdates: new Map(),

  setSwarms: (swarms) => set({ swarms }),
  
  addSwarm: (swarm) => set((state) => ({ 
    swarms: [swarm, ...state.swarms] 
  })),
  
  updateSwarm: (id, updates) => set((state) => ({
    swarms: state.swarms.map((s) =>
      s.id === id ? { ...s, ...updates } : s
    ),
    selectedSwarm: state.selectedSwarm?.id === id
      ? { ...state.selectedSwarm, ...updates }
      : state.selectedSwarm,
  })),
  
  removeSwarm: (id) => set((state) => ({
    swarms: state.swarms.filter((s) => s.id !== id),
    selectedSwarm: state.selectedSwarm?.id === id ? null : state.selectedSwarm,
  })),
  
  setSelectedSwarm: (swarm) => set({ selectedSwarm: swarm }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  connectWebSocket: (swarmId, token) => {
    const ws = new WebSocket(`${import.meta.env.VITE_WS_URL || 'wss://mindcore.mindaeon.com'}/ws/swarms/${swarmId}?token=${token}`)
    
    ws.onopen = () => {
      console.log('WebSocket connected')
    }
    
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data)
      get().handleWebSocketMessage(message)
    }
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      set({ error: 'WebSocket connection error' })
    }
    
    ws.onclose = () => {
      console.log('WebSocket disconnected')
      set({ websocket: null })
    }
    
    set({ websocket: ws })
  },
  
  disconnectWebSocket: () => {
    const { websocket } = get()
    if (websocket) {
      websocket.close()
      set({ websocket: null })
    }
  },
  
  handleWebSocketMessage: (message) => {
    const { updateSwarm } = get()
    
    switch (message.type) {
      case 'phase_change':
      case 'decomposition_complete':
      case 'agent_deployed':
      case 'agent_complete':
      case 'quality_result':
        // Update live state
        set((state) => ({
          liveUpdates: new Map(state.liveUpdates).set(message.type, message)
        }))
        break
        
      case 'swarm_complete':
      case 'swarm.complete':
        updateSwarm(message.data.swarmId, {
          status: 'complete',
          results: message.data.results,
        })
        break
        
      case 'swarm.error':
        updateSwarm(message.data.swarmId, {
          status: 'error',
        })
        break
    }
  },

  getSwarmsByStatus: (status) => {
    return get().swarms.filter((s) => s.status === status)
  },
  
  getActiveSwarms: () => {
    return get().swarms.filter((s) => 
      ['planning', 'executing', 'synthesizing'].includes(s.status)
    )
  },
}))
